/**
 * AdaptationsSection — O1 anpassningsuppföljning.
 *
 * Visas i deltagarens 360-vy som egen sektion mellan Processvy och övriga sektioner.
 * Skapas automatiskt från kartläggning steg 4 vid fastställning.
 * Manuella rader kan läggas till direkt.
 */
import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogFooter, DialogTrigger, DialogDescription,
} from '../ui/dialog';
import {
  Plus, AlertTriangle, CheckCircle2, Clock, XCircle,
} from 'lucide-react';
import api from '../../lib/api';
import { toast } from 'sonner';


const STATUS_LABELS = {
  rekommenderad: 'Rekommenderad',
  beslutad: 'Beslutad',
  'genomförd': 'Genomförd',
  avbruten: 'Avbruten',
};

const STATUS_STYLES = {
  rekommenderad: 'bg-muted text-muted-foreground',
  beslutad: 'bg-blue-100 text-blue-700',
  'genomförd': 'bg-success/15 text-success',
  avbruten: 'bg-red-100 text-red-700 line-through',
};

const PRIORITY_DOT = {
  'hög': { color: 'bg-red-500', label: 'Hög' },
  medel: { color: 'bg-yellow-500', label: 'Medel' },
  'låg': { color: 'bg-gray-400', label: 'Låg' },
};

const MAIN_GROUPS = [
  'Fysisk',
  'Psykosocial',
  'Pedagogisk',
  'Organisatorisk',
  'Kommunikationsstöd',
  'Handledarstöd',
];

const TABS = [
  { id: 'all', label: 'Alla' },
  { id: 'rekommenderad', label: 'Rekommenderade' },
  { id: 'beslutad', label: 'Beslutade' },
  { id: 'genomförd', label: 'Genomförda' },
];


const formatDate = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString('sv-SE'); } catch { return iso; }
};

const daysBetween = (iso) => {
  if (!iso) return null;
  const then = new Date(iso);
  const now = new Date();
  return Math.floor((now - then) / (1000 * 60 * 60 * 24));
};


const AdaptationCard = ({ adaptation, onUpdate, onDelete, canDelete }) => {
  const [expanded, setExpanded] = useState(false);
  const [confirmingComplete, setConfirmingComplete] = useState(false);
  const [updating, setUpdating] = useState(false);

  const daysSinceDecided = adaptation.status === 'beslutad' && adaptation.decided_date
    ? daysBetween(adaptation.decided_date)
    : null;
  const isOverdue = daysSinceDecided !== null && daysSinceDecided > 30;

  const updateStatus = async (newStatus) => {
    setUpdating(true);
    try {
      const resp = await api.patch(
        `/participants/${adaptation.participant_id}/adaptations/${adaptation.id}`,
        { status: newStatus },
      );
      onUpdate(resp.data);
      toast.success(`Status uppdaterad till ${STATUS_LABELS[newStatus]}`);
    } catch {
      toast.error('Kunde inte uppdatera status');
    }
    setUpdating(false);
    setConfirmingComplete(false);
  };

  const priorityCfg = PRIORITY_DOT[adaptation.priority];

  return (
    <Card className="border-2">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex-1 min-w-0">
            <p className="font-bold text-base truncate">{adaptation.adaptation_name}</p>
            <div className="flex items-center gap-2 flex-wrap mt-1.5">
              {adaptation.main_group && (
                <span className="text-xs px-2 py-0.5 rounded-full bg-muted text-muted-foreground">
                  {adaptation.main_group}
                </span>
              )}
              {priorityCfg && (
                <span className="inline-flex items-center gap-1.5 text-xs font-medium">
                  <span className={`w-2 h-2 rounded-full ${priorityCfg.color}`} />
                  {priorityCfg.label}
                </span>
              )}
              <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${STATUS_STYLES[adaptation.status]}`}>
                {STATUS_LABELS[adaptation.status]}
              </span>
              {isOverdue && (
                <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-orange-100 text-orange-700 font-medium">
                  <AlertTriangle size={11} /> Väntar på genomförande ({daysSinceDecided} d)
                </span>
              )}
            </div>
          </div>

          <div className="flex gap-1 shrink-0">
            {adaptation.status === 'rekommenderad' && (
              <Button size="sm" variant="outline" disabled={updating}
                onClick={() => updateStatus('beslutad')}>
                Besluta
              </Button>
            )}
            {adaptation.status === 'beslutad' && (
              <Button size="sm" variant="outline" disabled={updating}
                onClick={() => setConfirmingComplete(true)}>
                Markera genomförd
              </Button>
            )}
            {adaptation.status !== 'avbruten' && adaptation.status !== 'genomförd' && (
              <Button size="sm" variant="ghost" disabled={updating}
                onClick={() => updateStatus('avbruten')}
                aria-label="Avbryt anpassning">
                <XCircle size={14} />
              </Button>
            )}
            {canDelete && (
              <Button size="sm" variant="ghost" disabled={updating}
                onClick={() => onDelete(adaptation.id)}
                aria-label="Radera anpassning">
                <span className="text-destructive text-xs">Ta bort</span>
              </Button>
            )}
          </div>
        </div>

        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          {adaptation.decided_date && (
            <span>Beslutad: {formatDate(adaptation.decided_date)}</span>
          )}
          {adaptation.implemented_date && (
            <span className="text-success font-medium inline-flex items-center gap-1">
              <CheckCircle2 size={12} /> Genomförd: {formatDate(adaptation.implemented_date)}
            </span>
          )}
          {adaptation.source === 'assessment' && (
            <span className="ml-auto italic">Från kartläggning</span>
          )}
        </div>

        {(adaptation.notes || expanded) && (
          <div>
            <button
              type="button"
              className="text-xs text-primary hover:underline"
              onClick={() => setExpanded(!expanded)}
            >
              {expanded ? 'Dölj detaljer' : 'Visa detaljer'}
            </button>
            {expanded && adaptation.notes && (
              <p className="mt-2 text-sm text-muted-foreground whitespace-pre-line">
                {adaptation.notes}
              </p>
            )}
          </div>
        )}

        <Dialog open={confirmingComplete} onOpenChange={setConfirmingComplete}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Markera som genomförd</DialogTitle>
              <DialogDescription>
                Är du säker på att du vill markera "{adaptation.adaptation_name}" som genomförd?
                Datumet för genomförande sätts till idag.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setConfirmingComplete(false)}>
                Avbryt
              </Button>
              <Button onClick={() => updateStatus('genomförd')} disabled={updating}>
                Bekräfta
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
};


const AddAdaptationForm = ({ participantId, onAdded, onCancel }) => {
  const [form, setForm] = useState({
    adaptation_name: '',
    main_group: '',
    priority: 'medel',
    status: 'rekommenderad',
    notes: '',
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.adaptation_name.trim()) {
      toast.error('Anpassningsnamn krävs');
      return;
    }
    setSaving(true);
    try {
      const resp = await api.post(`/participants/${participantId}/adaptations`, form);
      onAdded(resp.data);
      toast.success('Anpassning skapad');
    } catch {
      toast.error('Kunde inte skapa anpassning');
    }
    setSaving(false);
  };

  return (
    <form onSubmit={submit} className="space-y-3 p-4 border-2 border-dashed rounded-lg">
      <div className="space-y-1.5">
        <Label htmlFor="adapt-name">Anpassning *</Label>
        <Input
          id="adapt-name"
          value={form.adaptation_name}
          onChange={(e) => setForm({ ...form, adaptation_name: e.target.value })}
          placeholder="Tex. Tydlig struktur eller anpassad arbetstid"
          required
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="space-y-1.5">
          <Label htmlFor="adapt-group">Huvudgrupp</Label>
          <select
            id="adapt-group"
            className="w-full h-10 rounded-md border border-input px-3 text-sm"
            value={form.main_group}
            onChange={(e) => setForm({ ...form, main_group: e.target.value })}
          >
            <option value="">— Välj —</option>
            {MAIN_GROUPS.map(g => <option key={g} value={g}>{g}</option>)}
          </select>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="adapt-prio">Prioritet</Label>
          <select
            id="adapt-prio"
            className="w-full h-10 rounded-md border border-input px-3 text-sm"
            value={form.priority}
            onChange={(e) => setForm({ ...form, priority: e.target.value })}
          >
            <option value="hög">Hög</option>
            <option value="medel">Medel</option>
            <option value="låg">Låg</option>
          </select>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="adapt-status">Status</Label>
          <select
            id="adapt-status"
            className="w-full h-10 rounded-md border border-input px-3 text-sm"
            value={form.status}
            onChange={(e) => setForm({ ...form, status: e.target.value })}
          >
            <option value="rekommenderad">Rekommenderad</option>
            <option value="beslutad">Beslutad</option>
          </select>
        </div>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="adapt-notes">Anteckningar</Label>
        <Textarea
          id="adapt-notes"
          value={form.notes}
          onChange={(e) => setForm({ ...form, notes: e.target.value })}
          rows={2}
        />
      </div>

      <div className="flex gap-2 justify-end">
        <Button type="button" variant="outline" onClick={onCancel}>Avbryt</Button>
        <Button type="submit" disabled={saving}>
          {saving ? 'Sparar...' : 'Lägg till'}
        </Button>
      </div>
    </form>
  );
};


const AdaptationsSection = ({ participantId, currentUserRole }) => {
  const [adaptations, setAdaptations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('all');
  const [showForm, setShowForm] = useState(false);

  const canDelete = currentUserRole === 'customer_admin' || currentUserRole === 'super_admin';

  useEffect(() => {
    if (!participantId) return;
    setLoading(true);
    api.get(`/participants/${participantId}/adaptations`)
      .then(r => setAdaptations(r.data || []))
      .catch(() => setAdaptations([]))
      .finally(() => setLoading(false));
  }, [participantId]);

  const handleAdded = (row) => {
    setAdaptations(prev => [row, ...prev]);
    setShowForm(false);
  };

  const handleUpdate = (updated) => {
    setAdaptations(prev => prev.map(a => a.id === updated.id ? updated : a));
  };

  const handleDelete = async (adaptationId) => {
    if (!window.confirm('Är du säker på att du vill radera anpassningen?')) return;
    try {
      await api.delete(`/participants/${participantId}/adaptations/${adaptationId}`);
      setAdaptations(prev => prev.filter(a => a.id !== adaptationId));
      toast.success('Anpassning raderad');
    } catch {
      toast.error('Kunde inte radera');
    }
  };

  const filtered = tab === 'all'
    ? adaptations
    : adaptations.filter(a => a.status === tab);

  const counts = {
    all: adaptations.length,
    rekommenderad: adaptations.filter(a => a.status === 'rekommenderad').length,
    beslutad: adaptations.filter(a => a.status === 'beslutad').length,
    'genomförd': adaptations.filter(a => a.status === 'genomförd').length,
  };

  return (
    <Card data-testid="adaptations-section">
      <CardHeader className="border-b pb-4 flex flex-row items-center justify-between gap-3">
        <CardTitle className="font-heading font-bold text-xl">Anpassningar och stöd</CardTitle>
        <Button size="sm" onClick={() => setShowForm(!showForm)} variant={showForm ? 'outline' : 'default'}>
          <Plus size={14} className="mr-1" />
          {showForm ? 'Stäng' : 'Lägg till'}
        </Button>
      </CardHeader>
      <CardContent className="pt-4 space-y-4">
        {showForm && (
          <AddAdaptationForm
            participantId={participantId}
            onAdded={handleAdded}
            onCancel={() => setShowForm(false)}
          />
        )}

        <div className="flex gap-2 flex-wrap">
          {TABS.map(t => (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                tab === t.id
                  ? 'bg-primary text-[#333333]'
                  : 'bg-muted text-muted-foreground hover:bg-primary/20'
              }`}
            >
              {t.label} ({counts[t.id] || 0})
            </button>
          ))}
        </div>

        {loading ? (
          <p className="text-sm text-muted-foreground py-4">Laddar anpassningar...</p>
        ) : filtered.length === 0 ? (
          <p className="text-sm text-muted-foreground py-6 text-center">
            <Clock size={16} className="inline mr-2" />
            {adaptations.length === 0
              ? 'Inga anpassningar registrerade. Lägg till manuellt eller fastställ en kartläggning.'
              : 'Inga anpassningar med denna status.'}
          </p>
        ) : (
          <div className="space-y-3">
            {filtered.map(a => (
              <AdaptationCard
                key={a.id}
                adaptation={a}
                onUpdate={handleUpdate}
                onDelete={handleDelete}
                canDelete={canDelete}
              />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AdaptationsSection;
